import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';

interface AudioMeterProps {
  audioElement: HTMLAudioElement | null;
  audioContext?: AudioContext;
  volume?: number; // 0-1 range
}

const AudioMeter: React.FC<AudioMeterProps> = ({ audioElement, audioContext, volume = 1 }) => {
  const [leftLevel, setLeftLevel] = useState(0);
  const [rightLevel, setRightLevel] = useState(0);
  const [leftPeak, setLeftPeak] = useState(0);
  const [rightPeak, setRightPeak] = useState(0);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const sourceRef = useRef<MediaElementAudioSourceNode | null>(null);
  const gainNodeRef = useRef<GainNode | null>(null);
  const animationRef = useRef<number | null>(null);
  const leftPeakTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const rightPeakTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!audioElement || !audioContext) return;

    // Cleanup function to prevent recreation on re-render
    let mounted = true;

    try {
      // Create or reuse source node
      if (!sourceRef.current && mounted) {
        const source = audioContext.createMediaElementSource(audioElement);
        sourceRef.current = source;

        // Create analyser node
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.8;
        analyserRef.current = analyser;

        // Create gain node for volume control
        const gainNode = audioContext.createGain();
        gainNode.gain.value = volume;
        gainNodeRef.current = gainNode;

        // Connect: source -> analyser -> gainNode -> destination
        // This ensures audio plays while we analyze it, with volume control
        source.connect(analyser);
        analyser.connect(gainNode);
        gainNode.connect(audioContext.destination);
      }

      if (!analyserRef.current) return;

      // Start analyzing
      const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);

      const updateMeter = () => {
        if (analyserRef.current) {
          analyserRef.current.getByteFrequencyData(dataArray);

          // Calculate average level (RMS-like)
          let sum = 0;
          for (let i = 0; i < dataArray.length; i++) {
            sum += dataArray[i];
          }
          const average = sum / dataArray.length;
          const normalizedLevel = (average / 255) * 100;

          // For stereo, we'll use the same data for now
          // In a more sophisticated implementation, we'd analyze each channel separately
          setLeftLevel(normalizedLevel);
          setRightLevel(normalizedLevel * 0.95); // Slight variation for visual effect

          // Update peaks
          if (normalizedLevel > leftPeak) {
            setLeftPeak(normalizedLevel);
            if (leftPeakTimeoutRef.current) clearTimeout(leftPeakTimeoutRef.current);
            leftPeakTimeoutRef.current = setTimeout(() => setLeftPeak(0), 1500);
          }

          if (normalizedLevel * 0.95 > rightPeak) {
            setRightPeak(normalizedLevel * 0.95);
            if (rightPeakTimeoutRef.current) clearTimeout(rightPeakTimeoutRef.current);
            rightPeakTimeoutRef.current = setTimeout(() => setRightPeak(0), 1500);
          }
        }

        animationRef.current = requestAnimationFrame(updateMeter);
      };

      updateMeter();

      return () => {
        mounted = false;
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
        }
        if (leftPeakTimeoutRef.current) clearTimeout(leftPeakTimeoutRef.current);
        if (rightPeakTimeoutRef.current) clearTimeout(rightPeakTimeoutRef.current);
      };
    } catch (error) {
      console.error('Error setting up audio meter:', error);
    }
  }, [audioElement, audioContext]);

  // Update gain node when volume changes
  useEffect(() => {
    if (gainNodeRef.current) {
      gainNodeRef.current.gain.value = volume;
    }
  }, [volume]);

  const getMeterColor = (level: number): string => {
    if (level > 90) return '#f44336'; // Red
    if (level > 75) return '#ff9800'; // Orange
    if (level > 50) return '#fdd835'; // Yellow
    return '#4caf50'; // Green
  };

  const MeterBar = ({ label, level, peak }: { label: string; level: number; peak: number }) => (
    <Box sx={{ mb: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 }, mb: 0.5 }}>
        <Typography variant="caption" sx={{ minWidth: { xs: '15px', sm: '20px' }, fontSize: { xs: '0.65rem', sm: '0.7rem' }, fontWeight: 600, flexShrink: 0 }}>
          {label}
        </Typography>
        <Box sx={{ flex: 1, minWidth: 0, height: { xs: 20, sm: 24 }, bgcolor: '#1a1a1a', borderRadius: 0.5, position: 'relative', overflow: 'hidden' }}>
          {/* Level bar */}
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: `${level}%`,
              bgcolor: getMeterColor(level),
              transition: 'width 0.05s ease-out',
            }}
          />
          {/* Peak indicator */}
          {peak > 0 && (
            <Box
              sx={{
                position: 'absolute',
                left: `${peak}%`,
                top: 0,
                bottom: 0,
                width: '2px',
                bgcolor: '#fff',
              }}
            />
          )}
          {/* dB markers */}
          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', px: 0.5 }}>
            {[0, -6, -12, -18, -24].map((db) => (
              <Box
                key={db}
                sx={{
                  position: 'absolute',
                  left: `${((100 + db * 4) / 100) * 100}%`,
                  height: '50%',
                  width: '1px',
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                }}
              />
            ))}
          </Box>
        </Box>
        <Typography variant="caption" sx={{ minWidth: { xs: '40px', sm: '45px' }, fontSize: { xs: '0.65rem', sm: '0.7rem' }, fontFamily: 'monospace', flexShrink: 0 }}>
          {level > 1 ? `${(20 * Math.log10(level / 100)).toFixed(1)}dB` : '-∞'}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{
      p: { xs: 1.5, sm: 2 },
      bgcolor: 'background.paper',
      borderRadius: 1,
      border: '1px solid',
      borderColor: 'divider',
      overflow: 'hidden',
      width: '100%'
    }}>
      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 1.5, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
        Peak Meter
      </Typography>
      <MeterBar label="L" level={leftLevel} peak={leftPeak} />
      <MeterBar label="R" level={rightLevel} peak={rightPeak} />
      <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', px: { xs: 2, sm: 3.5 } }}>
        {[0, -6, -12, -18, -24].map((db) => (
          <Typography
            key={db}
            variant="caption"
            sx={{ fontSize: { xs: '0.6rem', sm: '0.65rem' }, color: 'text.secondary' }}
          >
            {db}
          </Typography>
        ))}
      </Box>
    </Box>
  );
};

export default AudioMeter;
